import { Injectable, inject } from '@angular/core';
import { Observable, from, map, of } from 'rxjs';
import { BillingScope } from '../billing-consts';
import { isChecklistDone } from '../lib/is-checklist-done';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { LoggedInAnalyticsService } from './loggedin-analytics.service';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';

type CheckListItemTemplate = { key: string; title: string; description: string; scope?: BillingScope };

@Injectable({
  providedIn: 'root',
})
export class ChecklistService {
  private state = inject(StateService);
  private tenantService = inject(TenantService);
  private permissionService = inject(PermissionService);
  private checklistItems: CheckListItemTemplate[] = [
    {
      key: 'visitDash',
      title: 'Visit the Galleries Page',
      description: '',
    },
    {
      key: 'setWorkspaceName',
      title: '',
      description: '',
    },
    {
      key: 'uploadAssets',
      title: 'Upload Media',
      description: 'First things first: drop some assets into your gallery to start uploading.',
    },
    {
      key: 'setCover',
      title: 'Set Cover Asset',
      description: 'Drag an existing asset to the header area or upload a separate asset.',
    },
    {
      key: 'uploadLogo',
      title: 'Upload Logo',
      description: 'Navigate to your branding settings and upload your logo.',
      scope: 'tenant:logo-upload',
    },
    {
      key: 'firstFeedback',
      title: 'Get Feedback',
      description: 'Share a gallery with active review options and get your first feedback.',
    },
    {
      key: 'deliverAssets',
      title: 'Transfer Media',
      description: 'Share a gallery with downloads enabled and deliver some assets.',
    },
  ];
  private analytics = inject(PICFLOW_ANALYTICS);
  private loggedInAnalytics = inject(LoggedInAnalyticsService);

  private getCheckListItems() {
    return this.checklistItems.filter(item => {
      return !item.scope || this.permissionService.canDisplayElement(item.scope);
    });
  }

  private fixChecklist() {
    const activeTenant = this.state.activeTenant$?.value;
    if (activeTenant.onboarding_checklist?.done) return activeTenant.onboarding_checklist;
    const checklist = activeTenant.onboarding_checklist || {};
    this.getCheckListItems().forEach(item => {
      if (!checklist[item.key]) checklist[item.key] = false;
    });
    return checklist;
  }

  public markItemAsDone(key: string): Observable<undefined> {
    const activeTenant = this.state.activeTenant$?.value;
    if (!activeTenant) return of(undefined);
    if (!this.permissionService.hasRolePermissionLegacy('tenants:update')) return of(undefined);
    if (!this.checklistItems.find(i => i.key === key)) {
      console.error('Unknown checklist item');
      return of(undefined);
    }
    const onboarding_checklist = this.fixChecklist();
    this.tenantService.updateActiveTenantLocal({ onboarding_checklist });

    if (onboarding_checklist[key]) return of(undefined);
    onboarding_checklist[key] = true;

    const items = this.getCheckListItems();
    if (!items.find(item => !activeTenant.onboarding_checklist[item.key])) {
      onboarding_checklist.done = true;
    }

    return from(
      this.tenantService.updateTenant({
        onboarding_checklist,
      })
    ).pipe(
      map(() => {
        this.analytics.track('checklist item done', false, { name: key });
        if (activeTenant.onboarding_checklist.done) {
          this.analytics.track('checklist done', false);
        }
        return undefined;
      })
    );
  }

  public isDone() {
    return isChecklistDone(this.state.activeTenant$?.value);
  }
}
